import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import endpoints from '../../../../../../api/endpoints';
import { BusinessError } from '../../../../../common/business-error';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import {
  AddFundsToTheWalletOrderData,
  AddFundsToTheWalletOrderRequest,
  AddFundsToTheWalletOrderResponse
} from '../interfaces';
import { getCreateAnOrderBusinessError, BUSINESS_ERRORS } from '../businessErrors';

type DialogError = BusinessError | null;

interface CreateAnOrderProps {
  orderData: AddFundsToTheWalletOrderData;
  onSuccess: (responseData: AddFundsToTheWalletOrderResponse) => void;
}

export const useCreateAnOrder = () => {
  const [createAnOrderLoading, setLoading] = useState<boolean>(false);
  const [createAnOrderError, setError] = useState<DialogError>(null);
  const [operationId, setOperationId] = useState<string>(uuidv4());
  const [numberOfTries, setNumberOfTries] = useState<number>(1);

  const handleCreateAnOrder = async ({ orderData, onSuccess }: CreateAnOrderProps) => {
    try {
      setLoading(true);
      setError(null);

      const walletOrderRequest: AddFundsToTheWalletOrderRequest = { ...orderData, operationId };

      const response = await fetchWithTokenRefresh(
        endpoints.walletCreditExternalTransferOrder,
        addParams('POST', walletOrderRequest)
      );

      if (response.status !== 200) {
        await handleResponseError(response);
      }

      const jsonResponse = await response.json();
      onSuccess(jsonResponse);

      setLoading(false);
      setNumberOfTries(1);
    } catch (e) {
      if (e === BUSINESS_ERRORS.OPERATION_ALREADY_EXISTS && numberOfTries <= 3) {
        setOperationId(uuidv4());
        setNumberOfTries(numberOfTries + 1);
        handleCreateAnOrder({ orderData, onSuccess });
      }

      const { message, severity } = getCreateAnOrderBusinessError(e);

      setError({ message, severity });
      setLoading(false);
      setNumberOfTries(0);
    }
  };

  return { createAnOrderLoading, createAnOrderError, handleCreateAnOrder };
};
